/**
 * A project protocol log, through the same log viewer path as a pipeline item: an action run
 * ('run:<runId>') or a deploy's last log ('deploy:<targetId>'), streamed from the machine that
 * holds it. Demo mode shows plausible lines instead.
 */
import { Dialog, LogViewer } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { useLogStream } from '../../lib/useLogStream.ts';

export const runLogRef = (runId: string) => `run:${runId}`;
export const deployLogRef = (targetId: string) => `deploy:${targetId}`;

export function ProtocolLog({ logRef, label, demo, live, className = 'h-64' }: { logRef: string; label: string; demo: string; live?: boolean; className?: string }) {
  const mode = useOrgus((s) => s.mode);
  const stream = useLogStream({ type: 'pipelineItem', id: logRef }, mode === 'live');
  if (mode === 'mock') return <LogViewer label={label} text={demo} className={className} />;
  return (
    <LogViewer
      label={label}
      text={stream.log?.text ?? ''}
      live={live && !stream.log?.ended}
      loading={stream.loading}
      error={stream.error}
      onRetry={stream.retry}
      emptyText="No output yet."
      className={className}
    />
  );
}

/** A deploy's last log, in a dialog over the project drawer. */
export function DeployLogDialog({ target, onClose }: { target: { id: string; label: string; version: string | null; logRef: string } | null; onClose: () => void }) {
  if (!target) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()} size="xl" title={`${target.label} deploy log`} description={target.version ? `Version ${target.version}` : undefined}>
      <ProtocolLog
        logRef={target.logRef}
        label={`${target.label} deploy log`}
        demo={['Building console from main', 'vite build: 412 modules', 'Build output: dist is empty', 'Uploading to Pages', 'Error: the upload has no files', 'Deploy failed'].join('\n')}
        className="h-112"
      />
    </Dialog>
  );
}
